import axios from "axios";
import { useEffect, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { baseurl } from "../../Baseurl";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import axiosInstance from "../../api/axiosInstance";

export default function EditRxList() {
  const location = useLocation();
  const navigate = useNavigate();

  // Keep form state in snake_case to match backend API
  const [formData, setFormData] = useState({
    medicine_name: "",
    strength: "",
    unit: "",
    pharmaceutical_form: "",
    frequency: "",
    duration: "",
    notes: "",
    route: "",
    product_type: "",
    active_substances: "",
    packet_size: "",
  });

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [rxId, setRxId] = useState("");
  const [allergies, setAllergies] = useState([]);
  const [selectedWarnings, setSelectedWarnings] = useState([]);
  const [loadingAllergies, setLoadingAllergies] = useState(false);
  const [allergiesError, setAllergiesError] = useState(false);

  useEffect(() => {
    // Expecting navigation state from Pharmacy: navigate("/Admin/EditRxList", { state: { Rx: item } })
    const rx = location.state?.Rx;
    if (rx) {
      setRxId(rx.rxId || rx.id || "");
      setFormData({
        medicine_name: rx.medicineName || "",
        strength: rx.strength || "",
        unit: rx.unit || "",
        pharmaceutical_form: rx.form || rx.pharmaceutical_form || "",
        frequency: rx.frequency || "",
        duration: rx.duration || "",
        notes: rx.notes || "",
        route: rx.route || "",
        product_type: rx.productType || rx.product_type || "",
        active_substances: rx.activeSubstances || rx.active_substances || "",
        packet_size: rx.packet_size || "",
      });

      // Pre-populate selected allergies if available on rx
      const rxAllergies = rx.allergies || rx.Allergies || rx.warningList || [];
      if (Array.isArray(rxAllergies)) {
        setSelectedWarnings(
          rxAllergies.map((a) => ({
            id: a?.id ?? a?.allergyId ?? a?.value,
            allergyName: a?.allergyName || a?.name || a?.label || a?.warningName || "",
          }))
        );
      }
    }
  }, [location.state]);

  useEffect(() => {
    setLoadingAllergies(true);
    setAllergiesError(false);
    axiosInstance
      .get(`getAllAllergies`)
      .then((res) => {
        if (res.data?.success && Array.isArray(res.data?.data)) {
          setAllergies(res.data.data);
        } else {
          setAllergiesError(true);
        }
      })
      .catch(() => setAllergiesError(true))
      .finally(() => setLoadingAllergies(false));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const err = {};
    const required = [
      "medicine_name",
      "strength",
      "unit",
      "pharmaceutical_form",
      "frequency",
      "duration",
      "notes",
      "route",
      "product_type",
      "active_substances",
      "packet_size",
    ];

    required.forEach((k) => {
      if (!String(formData[k] ?? "").trim()) err[k] = "Required";
    });

    if (!rxId) err.rxId = "Missing Rx ID";
    return err;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const found = validate();
    if (Object.keys(found).length > 0) {
      setErrors(found);
      setSubmitted(false);
      return;
    }

    try {
      const url = `updateRxMedicine/${rxId}`;
      const payload = {
        ...formData,
        allergies: selectedWarnings
          .map((opt) => opt?.id ?? opt?.allergyId ?? opt?.value)
          .filter((v) => v !== undefined && v !== null),
      };
      const res = await axiosInstance.put(url, payload);
      if (res.data?.success) {
        Swal.fire("Success!", "Rx medicine updated successfully!", "success").then(() => {
          navigate("/Admin/Pharmacy");
        });
      } else {
        Swal.fire("Error", res.data?.message || "Update failed", "error");
      }
    } catch (error) {
      console.error("Update Rx error:", error);
      Swal.fire(
        "Error",
        error.response?.data?.message || error.message || "Update failed",
        "error"
      );
    }
  };

  return (
    <div className="pc-container">
      <div className="pc-content">
        <div className="col-12">
          <div className="card table-card">
            <div className="tableHeader">
              <h5 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span
                  style={{ cursor: 'pointer', color: '#3f75c7', fontWeight: 'bold', fontSize: '1.5rem' }}
                  onClick={() => navigate(-1)}
                  title="Back"
                >
                  &larr;
                </span>
                Edit RX Standard
              </h5>
            </div>

            {submitted && (
              <div className="alert alert-success">Rx medicine updated successfully!</div>
            )}

            <form onSubmit={handleSubmit} className="row g-3 px-3 py-2 mb-3">
              {[
                "medicine_name",
                "strength",
                "unit",
                "pharmaceutical_form",
                "frequency",
                "duration",
                "notes",
                "route",
                "product_type",
                "active_substances",
                "packet_size",
              ].map((field) => (
                <div className="col-md-6" key={field}>
                  <label className="form-label">{field.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}</label>
                  <input
                    type="text"
                    name={field}
                    className="form-control"
                    value={formData[field]}
                    onChange={handleChange}
                  />
                  {errors[field] && (
                    <p className="text-danger">{errors[field]}</p>
                  )}
                </div>
              ))}

              <div className="col-md-12">
                <label className="form-label">Warnings</label>
                <Autocomplete
                  multiple
                  id="warnings-autocomplete-edit"
                  options={allergies}
                  disablePortal
                  getOptionLabel={(option) =>
                    option?.warningName || option?.allergyName || option?.name || option?.label || ""
                  }
                  isOptionEqualToValue={(option, value) => {
                    const optionId = option?.id ?? option?.allergyId ?? option?.value;
                    const valueId = value?.id ?? value?.allergyId ?? value?.value;
                    return optionId === valueId;
                  }}
                  value={selectedWarnings}
                  onChange={(_, newValue) => setSelectedWarnings(newValue || [])}
                  loading={loadingAllergies}
                  disabled={loadingAllergies || allergiesError}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Allergies"
                      placeholder="Search and select Allergies..."
                      error={!!errors?.allergies}
                      helperText={errors?.allergies}
                    />
                  )}
                />
                {allergiesError && (
                  <p className="text-danger">Failed to load allergies list.</p>
                )}
              </div>

              {errors.rxId && (
                <div className="col-12">
                  <p className="text-danger">{errors.rxId}</p>
                </div>
              )}

              <div className="col-12 text-center">
                <button type="submit" className="bgBtn">
                  Update RX
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}


